import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "@untitled-ui/icons-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Container from "@/components/containers/Container";
import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import Button from "@/components/forms/Button";
import type { ChildRouteContext } from "@/root";

import ObjectiveListTab from "@/modules/performance/user/components/objective/list/ObjectiveListTab";
import ObjectivesInboxList from "@/modules/performance/user/components/objective/list/ObjectivesInboxList";
import { fetchObjectivePeriodsQueryOptions } from "@/modules/performance/user/services";

const tabs = ["all", "my", "inbox", "archives"] as const;
type ObjectivesTab = (typeof tabs)[number];

export const Route = createFileRoute(
  "/_panel/_performance/performance/objectives/",
)({
  validateSearch: z.object({
    tab: z.enum(tabs).default("all"),
  }),
  beforeLoad: ({ context }) =>
    ({
      crumb: [
        {
          label: context.t("Objectives", { ns: "performance.user" }),
        },
      ],
    }) satisfies ChildRouteContext,
  loader: (opts) =>
    opts.context.queryClient.ensureQueryData(
      fetchObjectivePeriodsQueryOptions(),
    ),
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation("performance.user");
  const data = Route.useLoaderData();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();

  return (
    <Container
      title={t("Objective Setting")}
      description={t("Manage, create, or edit various processes")}
    >
      <TabGroup
        selectedIndex={tabs.indexOf(tab)}
        onChange={(index) =>
          navigate({ search: (prev) => ({ ...prev, tab: tabs[index] }) })
        }
      >
        <div className="flex items-end gap-7">
          <TabList className="min-w-0 flex-1">
            <Tab>{t("All Objectives")}</Tab>
            <Tab>{t("My Objectives")}</Tab>
            <Tab>{t("Inbox", { ns: "core.shared" })}</Tab>
            <Tab>{t("Archives")}</Tab>
          </TabList>
          <div className="mb-5">
            <Button
              startIcon={<Plus className="size-5" />}
              link={{ to: "/performance/objectives/create" }}
              disabled={!data || data.length === 0}
            >
              {t("New Objective")}
            </Button>
          </div>
        </div>
        <TabPanels>
          <TabPanel>
            <div>
              <ObjectiveListTab returnTab="all" />
            </div>
          </TabPanel>
          <TabPanel>
            <ObjectiveListTab onlyMe returnTab="my" />
          </TabPanel>
          <TabPanel>
            <ObjectivesInboxList />
          </TabPanel>
          <TabPanel>
            <div>
              <ObjectiveListTab expired returnTab="archives" />
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </Container>
  );
}

export type { ObjectivesTab };
